import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import {
  fetchBillingExportOverview,
  type BillingDateRange,
  type BillingProjectItem,
  type BillingExportOverview,
  type BillingPaymentStatusFilter,
} from "@/lib/billing-exports";
import { fetchCompanyProfile, fetchCompanySettings, type CompanyProfile, type CompanySettings } from "@/lib/company";
import { fetchDashboardData, type DashboardRange, type DashboardResponse } from "@/lib/dashboard";
import { fetchNotifications, type NotificationsResponse } from "@/lib/notifications";
import {
  fetchPriceBookItems,
  type PriceBookItem,
  type PriceBookItemType,
} from "@/lib/price-book";
import {
  fetchApprovals,
  fetchApprovalById,
  type ApprovalDetail,
  type ApprovalFilters,
  type ApprovalListItem,
} from "@/lib/approvals";
import {
  fetchClients,
  fetchProjectLabels,
  fetchProjects,
  type ClientItem,
  type ProjectLabel,
  type ProjectItem,
} from "@/lib/projects-clients";
import { fetchScheduleBlocks, type ScheduleBlocksResponse } from "@/lib/schedule";
import { fetchReportsOverview, type PendingAgingMode, type ReportsDateRange, type ReportsOverview } from "@/lib/reports";
import { fetchTeamMembers, type TeamMember } from "@/lib/team";
import { fetchTemplates, type ApiTemplate, type TemplateCategory } from "@/lib/templates";
import {
  APPROVAL_QUEUE_STATUSES,
  type ApprovalDeliveryMethod,
  type ApiTicketStatus,
  type ApiTicketType,
  fetchProjectTicketContext,
  type ProjectTicketContext,
} from "@/lib/tickets";

const LIST_STALE_TIME = 60 * 1000;
const DASHBOARD_STALE_TIME = 45 * 1000;
const SETTINGS_STALE_TIME = 2 * 60 * 1000;

export const queryKeys = {
  projects: (search = "", labelsKey = "") => ["projects", search, labelsKey] as const,
  projectLabels: () => ["project-labels"] as const,
  clients: (search = "") => ["clients", search] as const,
  tickets: (filters: {
    search: string;
    status: string;
    type: string;
    projectId: string;
    startDate: string;
    endDate: string;
  }) =>
    [
      "tickets",
      filters.search,
      filters.status,
      filters.type,
      filters.projectId,
      filters.startDate,
      filters.endDate,
    ] as const,
  approvals: (filters: {
    search: string;
    projectId: string;
    clientId: string;
    agingBucket: string;
    statusesKey: string;
    neverOpened: boolean;
    startDate: string;
    endDate: string;
  }) =>
    [
      "approvals",
      filters.search,
      filters.projectId,
      filters.clientId,
      filters.agingBucket,
      filters.statusesKey,
      filters.neverOpened ? "1" : "0",
      filters.startDate,
      filters.endDate,
    ] as const,
  approvalDetail: (id: string) => ["approval-detail", id] as const,
  dashboard: (range: DashboardRange) => ["dashboard", range] as const,
  billingOverview: (filters: {
    dateRange: BillingDateRange;
    status: BillingPaymentStatusFilter;
    projectId: string;
    search: string;
    startDate: string;
    endDate: string;
    includeArchived: string;
  }) =>
    [
      "billing-overview",
      filters.dateRange,
      filters.status,
      filters.projectId,
      filters.search,
      filters.startDate,
      filters.endDate,
      filters.includeArchived,
    ] as const,
  reportsOverview: (filters: {
    projectId: string;
    dateRange: ReportsDateRange;
    pendingAgingMode: PendingAgingMode;
    startDate: string;
    endDate: string;
  }) =>
    [
      "reports-overview",
      filters.projectId,
      filters.dateRange,
      filters.pendingAgingMode,
      filters.startDate,
      filters.endDate,
    ] as const,
  teamMembers: () => ["team-members"] as const,
  templates: (category = "", subType = "") => ["templates", category, subType] as const,
  companySettings: () => ["company-settings"] as const,
  companyProfile: () => ["company-profile"] as const,
  companyMe: () => ["company-me"] as const,
  priceBook: (search = "", type = "", includeArchived = "0") =>
    ["price-book", search, type, includeArchived] as const,
  notifications: (limit = 20) => ["notifications", limit] as const,
  scheduleBlocks: (date: string) => ["schedule-blocks", date] as const,
  projectScheduleBlocks: (projectId: string, futureOnly = false) =>
    ["project-schedule-blocks", projectId, futureOnly ? "1" : "0"] as const,
  projectTicketContext: (projectId: string) => ["project-ticket-context", projectId] as const,
};

export type TicketListItem = {
  id: string;
  ticketNumber: string;
  type: ApiTicketType;
  status: ApiTicketStatus;
  signatureStatus: "NOT_REQUESTED" | "PENDING" | "CAPTURED";
  title: string;
  createdAt: string;
  sentAt?: string | null;
  totalAmount: string;
  agingDays: number;
  project: {
    id: string;
    name: string;
    createdAt: string;
    assignedMember?: {
      id: string;
      fullName: string;
      email: string;
      role?: string;
    } | null;
  };
  client?: { id: string; name: string } | null;
  createdBy: { id: string; fullName: string; email: string };
  approvalRequestId?: string | null;
  approvalDeliveryMethod?: ApprovalDeliveryMethod | null;
  approvalRecipientPhone?: string | null;
  lastOpenedAt?: string | null;
  reminderCount?: number;
};

export type TicketsResponse = {
  page: number;
  pageSize: number;
  total: number;
  items: TicketListItem[];
};

export function useProjectTicketContextQuery(projectId: string) {
  return useQuery<ProjectTicketContext>({
    queryKey: queryKeys.projectTicketContext(projectId),
    queryFn: () => fetchProjectTicketContext(projectId),
    enabled: Boolean(projectId),
    staleTime: LIST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useProjects(params: string | { search?: string; labelIds?: string[] } = "", enabled = true) {
  const stable =
    typeof params === "string"
      ? {
          search: params.trim(),
          labelsKey: "",
          labelIds: [] as string[],
        }
      : {
          search: params.search?.trim() || "",
          labelsKey: [...(params.labelIds ?? [])].sort().join(","),
          labelIds: params.labelIds ?? [],
        };

  return useQuery({
    queryKey: queryKeys.projects(stable.search, stable.labelsKey),
    queryFn: () =>
      fetchProjects({
        search: stable.search,
        labelIds: stable.labelIds,
      }),
    enabled,
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useProjectLabels() {
  return useQuery({
    queryKey: queryKeys.projectLabels(),
    queryFn: () => fetchProjectLabels(),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useCompanyProfileQuery() {
  return useQuery<CompanyProfile>({
    queryKey: queryKeys.companyProfile(),
    queryFn: () => fetchCompanyProfile(),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useClients(search = "") {
  return useQuery({
    queryKey: queryKeys.clients(search),
    queryFn: () => fetchClients(search),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useTicketsQuery(params: {
  search: string;
  status: "ALL" | ApiTicketStatus;
  type?: "ALL" | ApiTicketType;
  projectId?: string;
  startDate?: string;
  endDate?: string;
}) {
  const stable = {
    search: params.search.trim(),
    status: params.status,
    type: params.type || "ALL",
    projectId: params.projectId || "",
    startDate: params.startDate || "",
    endDate: params.endDate || "",
  };

  return useQuery({
    queryKey: queryKeys.tickets(stable),
    queryFn: () => {
      const query = new URLSearchParams();
      if (stable.search) query.set("search", stable.search);
      if (stable.status !== "ALL") query.set("status", stable.status);
      if (stable.type !== "ALL") query.set("type", stable.type);
      if (stable.projectId) query.set("projectId", stable.projectId);
      if (stable.startDate) query.set("startDate", stable.startDate);
      if (stable.endDate) query.set("endDate", stable.endDate);
      query.set("pageSize", "50");

      return apiRequest<TicketsResponse>(`/tickets?${query.toString()}`, {
        method: "GET",
      });
    },
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useApprovalsQuery(params: {
  search: string;
  projectId: string;
  clientId: string;
  agingBucket: string;
  statuses: string[];
  neverOpened: boolean;
  startDate?: string;
  endDate?: string;
}) {
  const stable = {
    search: params.search.trim(),
    projectId: params.projectId,
    clientId: params.clientId,
    agingBucket: params.agingBucket,
    statusesKey: params.statuses.join(","),
    neverOpened: params.neverOpened,
    startDate: params.startDate || "",
    endDate: params.endDate || "",
  };

  return useQuery({
    queryKey: queryKeys.approvals(stable),
    queryFn: () =>
      fetchApprovals({
        search: stable.search || undefined,
        projectId: stable.projectId || undefined,
        clientId: stable.clientId || undefined,
        agingBucket: (stable.agingBucket as ApprovalFilters["agingBucket"]) || undefined,
        statuses: params.statuses.length ? (params.statuses as ApprovalFilters["statuses"]) : undefined,
        neverOpened: stable.neverOpened || undefined,
        startDate: stable.startDate || undefined,
        endDate: stable.endDate || undefined,
      }),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useApprovalDetail(id: string | null) {
  return useQuery({
    queryKey: queryKeys.approvalDetail(id || ""),
    queryFn: () => fetchApprovalById(id!),
    enabled: Boolean(id),
    staleTime: LIST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useDashboard(range: DashboardRange) {
  return useQuery({
    queryKey: queryKeys.dashboard(range),
    queryFn: () => fetchDashboardData({ range }),
    staleTime: DASHBOARD_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useScheduleBlocks(date: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduleBlocks(date),
    queryFn: () => fetchScheduleBlocks({ date }),
    enabled: enabled && Boolean(date),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useProjectScheduleBlocks(projectId: string | null, futureOnly = false, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projectScheduleBlocks(projectId || "", futureOnly),
    queryFn: () => fetchScheduleBlocks({ projectId: projectId!, futureOnly }),
    enabled: enabled && Boolean(projectId),
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });
}

export function useBillingOverview(params: {
  dateRange: BillingDateRange;
  status: BillingPaymentStatusFilter;
  projectId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  includeArchived?: boolean;
}) {
  const stable = {
    dateRange: params.dateRange,
    status: params.status,
    projectId: params.projectId || "",
    search: params.search?.trim() || "",
    startDate: params.startDate || "",
    endDate: params.endDate || "",
    includeArchived: params.includeArchived ? "1" : "0",
  };

  return useQuery({
    queryKey: queryKeys.billingOverview(stable),
    queryFn: () =>
      fetchBillingExportOverview({
        dateRange: stable.dateRange,
        status: stable.status,
        projectId: stable.projectId || undefined,
        search: stable.search || undefined,
        startDate: stable.startDate || undefined,
        endDate: stable.endDate || undefined,
        includeArchived: stable.includeArchived === "1",
      }),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useReportsOverviewQuery(params: {
  projectId?: string;
  dateRange: ReportsDateRange;
  pendingAgingMode: PendingAgingMode;
  startDate?: string;
  endDate?: string;
}) {
  const stable = {
    projectId: params.projectId || "",
    dateRange: params.dateRange,
    pendingAgingMode: params.pendingAgingMode,
    startDate: params.startDate || "",
    endDate: params.endDate || "",
  };

  return useQuery({
    queryKey: queryKeys.reportsOverview(stable),
    queryFn: () =>
      fetchReportsOverview({
        projectId: stable.projectId || undefined,
        dateRange: stable.dateRange,
        pendingAgingMode: stable.pendingAgingMode,
        startDate: stable.startDate || undefined,
        endDate: stable.endDate || undefined,
      }),
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useTeamMembers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.teamMembers(),
    queryFn: () => fetchTeamMembers(),
    enabled,
    staleTime: LIST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useTemplatesQuery(filters: { category?: TemplateCategory; subType?: string } = {}) {
  const category = filters.category || "";
  const subType = filters.subType || "";
  return useQuery({
    queryKey: queryKeys.templates(category, subType),
    queryFn: () => fetchTemplates(filters),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useCompanySettingsQuery() {
  return useQuery({
    queryKey: queryKeys.companySettings(),
    queryFn: () => fetchCompanySettings(),
    staleTime: SETTINGS_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useCompanyMeQuery() {
  return useQuery({
    queryKey: queryKeys.companyMe(),
    queryFn: () =>
      apiRequest<{
        id: string;
        name: string;
        ownerFullName: string;
        email: string;
        phone?: string | null;
        website?: string | null;
        companyType?:
          | "GENERAL_CONTRACTOR_REMODELER"
          | "PLUMBING_CONTRACTOR"
          | "ELECTRICAL_CONTRACTOR"
          | "HVAC_CONTRACTOR"
          | "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR"
          | "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR"
          | "FLOORING_CONTRACTOR"
          | "PAINTING_CONTRACTOR"
          | "CONCRETE_CONTRACTOR"
          | "ROOFING_CONTRACTOR"
          | "LANDSCAPING_HARDSCAPING_CONTRACTOR"
          | null;
      }>("/companies/me", { method: "GET" }),
    staleTime: SETTINGS_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function usePriceBookItems(params: {
  search?: string;
  type?: PriceBookItemType | "";
  includeArchived?: boolean;
} = {}) {
  const stable = {
    search: params.search?.trim() || "",
    type: params.type || "",
    includeArchived: params.includeArchived ? "1" : "0",
  };

  return useQuery({
    queryKey: queryKeys.priceBook(stable.search, stable.type, stable.includeArchived),
    queryFn: () =>
      fetchPriceBookItems({
        search: stable.search || undefined,
        type: (stable.type as PriceBookItemType | "") || undefined,
        includeArchived: stable.includeArchived === "1",
      }),
    staleTime: LIST_STALE_TIME,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export function useNotificationsQuery(limit = 20) {
  return useQuery({
    queryKey: queryKeys.notifications(limit),
    queryFn: () => fetchNotifications(limit),
    staleTime: 0,
  });
}

export type {
  BillingExportOverview,
  BillingProjectItem,
  ReportsOverview,
  TeamMember,
  ApiTemplate,
  CompanySettings,
  PriceBookItem,
  DashboardResponse,
  ProjectItem,
  ProjectLabel,
  ClientItem,
  TicketsResponse,
  TicketListItem,
  ApprovalListItem,
  ApprovalDetail,
  NotificationsResponse,
};
